import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MentionTextarea } from '../components/MentionTextarea';
import { useUserNames } from '../context/UserNamesContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, GraduationCap, MapPin, Phone, Mail, Globe, Edit2, Save, X, MessageSquare, Trash2, Plus, Users as UsersIcon, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import ScheduleSection from '../components/schedule/ScheduleSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHOOL_TYPES = ['University', 'Community College', 'PT Program', 'PTA Program', 'OT Program', 'COTA Program', 'SLP Program', 'Career Fair', 'Other'];

export default function SchoolDetail() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [newNote, setNewNote] = useState('');
  const [noteMentions, setNoteMentions] = useState([]);
  const { resolveName } = useUserNames();

  const fetchSchool = async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/schools/${schoolId}`),
        axios.get(`${API}/prospects`, { params: { school_id: schoolId } }),
      ]);
      setSchool(s.data);
      setDraft(s.data);
      setProspects(p.data || []);
    } catch (e) {
      toast.error('Failed to load school');
      navigate('/recruiting');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSchool(); /* eslint-disable-next-line */ }, [schoolId]);

  const save = async () => {
    try {
      await axios.put(`${API}/schools/${schoolId}`, draft);
      toast.success('School updated');
      setEditing(false);
      fetchSchool();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/schools/${schoolId}/notes`, { text: newNote.trim(), mentions: noteMentions });
      setNewNote('');
      setNoteMentions([]);
      fetchSchool();
    } catch (e) { toast.error('Failed to add note'); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/schools/${schoolId}/notes/${noteId}`);
      fetchSchool();
    } catch (_) { toast.error('Failed to delete'); }
  };

  const deleteSchool = async () => {
    if (!window.confirm(`Delete school "${school.name}"? Prospects will keep their records but lose the school link.`)) return;
    try {
      await axios.delete(`${API}/schools/${schoolId}`);
      toast.success('School deleted');
      navigate('/recruiting');
    } catch (_) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading…</div>;
  if (!school) return null;

  const prospectsByYear = prospects.reduce((acc, p) => {
    const y = p.graduating_year || 'Unspecified';
    (acc[y] = acc[y] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="container mx-auto py-6 space-y-6 px-4">
      <button onClick={() => navigate('/recruiting')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-recruiting">
        <ArrowLeft size={14} /> Back to Recruiting
      </button>

      <Card className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex-1">
            {editing ? (
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={draft.name || ''} onChange={e => setDraft({ ...draft, name: e.target.value })} /></div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Type</Label>
                    <select value={draft.school_type || ''} onChange={e => setDraft({ ...draft, school_type: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                      {SCHOOL_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div><Label>Phone</Label><Input value={draft.phone || ''} onChange={e => setDraft({ ...draft, phone: e.target.value })} /></div>
                  <div><Label>Email</Label><Input value={draft.email || ''} onChange={e => setDraft({ ...draft, email: e.target.value })} /></div>
                  <div><Label>Website</Label><Input value={draft.website || ''} onChange={e => setDraft({ ...draft, website: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="md:col-span-2"><Label>Address</Label><Input value={draft.address || ''} onChange={e => setDraft({ ...draft, address: e.target.value })} /></div>
                  <div><Label>City</Label><Input value={draft.city || ''} onChange={e => setDraft({ ...draft, city: e.target.value })} /></div>
                  <div><Label>State</Label><Input value={draft.state || ''} onChange={e => setDraft({ ...draft, state: e.target.value })} maxLength={2} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Contact Person</Label><Input value={draft.contact_person || ''} onChange={e => setDraft({ ...draft, contact_person: e.target.value })} /></div>
                  <div><Label>Contact Email</Label><Input value={draft.contact_email || ''} onChange={e => setDraft({ ...draft, contact_email: e.target.value })} /></div>
                  <div><Label>Contact Phone</Label><Input value={draft.contact_phone || ''} onChange={e => setDraft({ ...draft, contact_phone: e.target.value })} /></div>
                </div>
                <div><Label>Description</Label><Textarea value={draft.description || ''} onChange={e => setDraft({ ...draft, description: e.target.value })} rows={2} /></div>
                <div className="flex gap-2">
                  <Button onClick={save} data-testid="save-school-btn"><Save size={14} className="mr-1" /> Save</Button>
                  <Button variant="ghost" onClick={() => { setEditing(false); setDraft(school); }}><X size={14} className="mr-1" /> Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold flex items-center gap-2"><GraduationCap size={22} /> {school.name}</h1>
                <p className="text-sm text-muted-foreground">{school.school_type}</p>
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  <div className="flex items-center gap-1"><MapPin size={13} className="text-muted-foreground" /> {[school.address, school.city, school.state].filter(Boolean).join(', ') || '—'}</div>
                  <div className="flex items-center gap-1"><Phone size={13} className="text-muted-foreground" /> {school.phone || '—'}</div>
                  <div className="flex items-center gap-1"><Mail size={13} className="text-muted-foreground" /> {school.email || '—'}</div>
                  {school.website && <a href={school.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline"><Globe size={13} /> Website</a>}
                </div>
                {school.contact_person && (
                  <div className="mt-3 p-3 bg-muted/30 rounded text-sm">
                    <p className="font-medium">{school.contact_person}</p>
                    <div className="text-xs text-muted-foreground">{school.contact_email || ''} {school.contact_phone ? `· ${school.contact_phone}` : ''}</div>
                  </div>
                )}
                {school.description && <p className="mt-3 text-sm text-muted-foreground">{school.description}</p>}
              </>
            )}
          </div>
          {!editing && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-school-btn"><Edit2 size={13} className="mr-1" /> Edit</Button>
              <Button variant="outline" size="sm" onClick={deleteSchool} data-testid="delete-school-btn" className="text-red-600 hover:text-red-700"><Trash2 size={13} /></Button>
            </div>
          )}
        </div>
      </Card>

      <Tabs defaultValue="prospects">
        <TabsList>
          <TabsTrigger value="prospects" data-testid="school-tab-prospects"><UsersIcon size={13} className="mr-1" /> Prospects ({prospects.length})</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="school-tab-schedule"><Calendar size={13} className="mr-1" /> Schedule</TabsTrigger>
          <TabsTrigger value="notes" data-testid="school-tab-notes"><MessageSquare size={13} className="mr-1" /> Notes ({(school.notes || []).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="prospects" className="mt-4">
          {prospects.length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground text-sm">No prospects from this school yet.</Card>
          ) : Object.entries(prospectsByYear).sort((a, b) => String(b[0]).localeCompare(String(a[0]))).map(([year, list]) => (
            <Card key={year} className="p-4 mb-3">
              <h4 className="font-semibold text-sm mb-2">Class of {year} ({list.length})</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {list.map(p => (
                  <Link key={p.id} to={`/ats/applicants/${p.id}`} className="border rounded p-2 hover:bg-muted/30 text-sm" data-testid={`school-prospect-${p.id}`}>
                    <p className="font-medium">{p.first_name} {p.last_name}</p>
                    <p className="text-xs text-muted-foreground">{p.discipline || '—'}{p.email ? ` · ${p.email}` : ''}</p>
                  </Link>
                ))}
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="schedule" className="mt-4">
          <ScheduleSection
            relatedType="school"
            relatedId={schoolId}
            defaultAttendee={null}
            title={`Events at ${school.name}`}
          />
        </TabsContent>

        <TabsContent value="notes" className="mt-4">
          <Card className="p-4">
            <div className="flex gap-2 mb-3">
              <MentionTextarea
                value={newNote}
                onChange={setNewNote}
                mentions={noteMentions}
                onMentionsChange={setNoteMentions}
                placeholder="Add a note about this school… type @ to tag a teammate"
                rows={3}
                testId="school-new-note"
                onSubmitShortcut={addNote}
              />
              <Button onClick={addNote} disabled={!newNote.trim()} data-testid="add-school-note"><Plus size={14} /></Button>
            </div>
            <div className="space-y-2">
              {(school.notes || []).slice().reverse().map(n => (
                <div key={n.id} className="border rounded p-3 hover:bg-muted/20" data-testid={`school-note-${n.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm whitespace-pre-wrap">{n.text}</p>
                      {n.mentions?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {n.mentions.map((m) => (
                            <span key={m.user_id} className="inline-flex items-center gap-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 rounded-full px-1.5 py-0.5">@{m.name}</span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{resolveName(n.author_name)} · {new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={() => deleteNote(n.id)} className="text-muted-foreground hover:text-red-500"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
              {(!school.notes || school.notes.length === 0) && <p className="text-center text-sm text-muted-foreground py-4">No notes yet.</p>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
